import React from "react";
import { Layout, Typography, Space, Avatar, Button, Dropdown, Tooltip } from "antd";
import { PoweroffOutlined, UserOutlined, SunOutlined, MoonOutlined, SettingOutlined } from '@ant-design/icons';
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useAI } from "../../context/AIContext";
import { useTheme } from "../../context/ThemeContext";
import { notification } from "antd";

const { Header: AntHeader } = Layout;

const Header = ({ onOpenApiDocs }) => {
  const { user } = useAuth();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { badgeVisible, clearBadge, final, lastResultType } = useAI();
  const { toggleTheme, isDark } = useTheme();

  const supportMenuItems = [
    { key: 'api-docs', label: 'API Docs', onClick: () => onOpenApiDocs?.() },
    { key: 'contact', label: 'Contact', onClick: () => navigate('/contact') },
    { key: 'about', label: 'About', onClick: () => navigate('/about') },
  ];

  React.useEffect(() => {
    if (!badgeVisible || !final) return;
    if (location.pathname.includes('aiDebugger') || location.pathname.includes('ai-assistant')) return;
    const sanitizePreview = (t) => (t || '').replace(/\*\*|__|\*|_|`|#+/g, '').replace(/\n+/g, ' ').trim().substring(0, 120) + ((t || '').length > 120 ? '...' : '');
    notification.destroy('ai-response');
    const isError = lastResultType === 'error';
    notification.open({
      key: 'ai-response',
      message: isError ? '❌ AI Response Failed' : '✨ AI Response Ready',
      description: sanitizePreview(final),
      type: isError ? 'error' : 'info',
      duration: isError ? 12 : 8,
      placement: 'topRight',
      onClick: () => { navigate('/aiDebugger'); clearBadge(); notification.destroy('ai-response'); },
      style: { cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
    });
  }, [badgeVisible, final, lastResultType, location.pathname, navigate, clearBadge]);

  const headerIconSize = 22;
  const iconBtnStyle = {
    width: 44,
    height: 44,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    color: isDark ? '#91caff' : '#1677ff',
    border: 'none',
    background: 'transparent',
    transition: 'all 0.2s ease',
  };

  const iconBtnHover = {
    color: '#1890ff',
    background: isDark ? 'rgba(24, 144, 255, 0.15)' : 'rgba(24, 144, 255, 0.08)',
  };

  return (
    <AntHeader
      style={{
        background: isDark
          ? 'linear-gradient(135deg, #0d1f33 0%, #0c1d2e 50%, #0a1828 100%)'
          : 'linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 50%, #e0f0ff 100%)',
        padding: "0 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: isDark ? "0 1px 2px rgba(0,0,0,0.3)" : "0 1px 2px rgba(24, 144, 255, 0.08)",
        minHeight: 56,
        height: 56,
        borderBottom: isDark ? '1px solid rgba(24, 144, 255, 0.2)' : '1px solid rgba(24, 144, 255, 0.15)',
      }}
    >
      <Typography.Text
        style={{
          margin: 0,
          fontSize: 15,
          fontWeight: 500,
          color: isDark ? '#e6f4ff' : '#0d2137',
          letterSpacing: '-0.01em',
        }}
      >
        Illuminating Your Digital Landscape
      </Typography.Text>

      {user && (
        <Space size={4} align="center">
          <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
            <Button
              type="text"
              icon={isDark ? <SunOutlined style={{ fontSize: headerIconSize }} /> : <MoonOutlined style={{ fontSize: headerIconSize }} />}
              onClick={toggleTheme}
              style={iconBtnStyle}
              onMouseEnter={(e) => Object.assign(e.currentTarget.style, iconBtnHover)}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = iconBtnStyle.color;
                e.currentTarget.style.background = iconBtnStyle.background;
              }}
            />
          </Tooltip>

          <span
            style={{
              width: 1,
              height: 24,
              background: isDark ? 'rgba(24, 144, 255, 0.35)' : 'rgba(24, 144, 255, 0.3)',
              margin: '0 6px',
            }}
          />

          <Tooltip title="Settings, API docs, and support">
            <Dropdown
              menu={{ items: supportMenuItems }}
              placement="bottomRight"
              dropdownStyle={{
                background: isDark ? '#0d1f33' : '#fff',
                border: isDark ? '1px solid rgba(24, 144, 255, 0.25)' : '1px solid rgba(24, 144, 255, 0.15)',
                borderRadius: 8,
                boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)',
              }}
            >
              <Button
                type="text"
                icon={<SettingOutlined style={{ fontSize: headerIconSize }} />}
                style={iconBtnStyle}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, iconBtnHover)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = iconBtnStyle.color;
                  e.currentTarget.style.background = iconBtnStyle.background;
                }}
              />
            </Dropdown>
          </Tooltip>

          <span
            style={{
              width: 1,
              height: 24,
              background: isDark ? 'rgba(24, 144, 255, 0.35)' : 'rgba(24, 144, 255, 0.3)',
              margin: '0 6px',
            }}
          />

          <Tooltip title="Account and logout">
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'user',
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
                        <UserOutlined style={{ color: isDark ? '#8b949e' : '#595959', fontSize: 18 }} />
                        <span style={{ color: isDark ? '#e6f4ff' : '#0d2137', fontWeight: 500, fontSize: 14 }}>
                          {user.username.charAt(0).toUpperCase() + user.username.slice(1)}
                        </span>
                      </div>
                    ),
                    disabled: true,
                  },
                  { type: 'divider' },
                  {
                    key: 'logout',
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#ff4d4f', fontWeight: 500, fontSize: 14 }}>
                        <PoweroffOutlined className="logout-icon" style={{ fontSize: 18 }} />
                        <span>Logout</span>
                      </div>
                    ),
                    onClick: () => logout(),
                  },
                ],
              }}
              placement="bottomRight"
              trigger={['click']}
              dropdownStyle={{
                background: isDark ? '#0d1f33' : '#fff',
                border: isDark ? '1px solid rgba(24, 144, 255, 0.25)' : '1px solid rgba(24, 144, 255, 0.15)',
                borderRadius: 8,
                boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(24, 144, 255, 0.12)',
                minWidth: 200,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 10px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  minHeight: 44,
                  ...iconBtnStyle,
                }}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, iconBtnHover)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = iconBtnStyle.color;
                  e.currentTarget.style.background = iconBtnStyle.background;
                }}
              >
                <Avatar
                  size={36}
                  icon={<UserOutlined style={{ fontSize: 18 }} />}
                  style={{
                    backgroundColor: isDark ? 'rgba(24, 144, 255, 0.5)' : '#1890ff',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: isDark ? '#e6f4ff' : '#0d2137',
                    maxWidth: 140,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.username.charAt(0).toUpperCase() + user.username.slice(1)}
                </span>
              </div>
            </Dropdown>
          </Tooltip>
        </Space>
      )}
    </AntHeader>
  );
};

export default Header;
